'use client';

import React, { useState, useRef, useEffect } from 'react';
import Header from '@/components/Header';

interface Message {
  id: number;
  senderId: string;
  text: string;
  time: string;
  read: boolean;
}

interface Conversation {
  id: number;
  name: string;
  avatar: string;
  role: 'host' | 'guest';
  property: string;
  propertyImage: string;
  checkIn: string;
  checkOut: string;
  lastMessage: string;
  lastTime: string;
  unread: number;
  bookingRef: string;
  bookingStatus: 'confirmed' | 'pending' | 'completed';
  messages: Message[];
}

const conversations: Conversation[] = [
  {
    id: 1,
    name: 'James Ssekandi',
    avatar: 'J',
    role: 'host',
    property: 'Luxury Villa Kololo',
    propertyImage: 'https://images.unsplash.com/photo-1564013799919-ab600027ffc6?w=80&h=80&fit=crop',
    checkIn: 'Aug 25, 2026',
    checkOut: 'Aug 30, 2026',
    lastMessage: 'The pool is heated and ready for your arrival!',
    lastTime: '10:32 AM',
    unread: 2,
    bookingRef: 'NDT-2026-0821',
    bookingStatus: 'confirmed',
    messages: [
      { id: 1, senderId: 'host', text: 'Hello! Welcome to Ndoto Stays. I\'m James, your host for the Luxury Villa Kololo.', time: '9:00 AM', read: true },
      { id: 2, senderId: 'me', text: 'Hi James! Really excited about our stay. Is early check-in possible?', time: '9:15 AM', read: true },
      { id: 3, senderId: 'host', text: 'Absolutely! I can arrange check-in from 11 AM. The villa will be fully prepared.', time: '9:30 AM', read: true },
      { id: 4, senderId: 'me', text: 'That\'s perfect, thank you so much!', time: '9:45 AM', read: true },
      { id: 5, senderId: 'host', text: 'The pool is heated and ready for your arrival!', time: '10:32 AM', read: false },
    ],
  },
  {
    id: 2,
    name: 'Grace Achieng',
    avatar: 'G',
    role: 'host',
    property: 'Lakeside Retreat Entebbe',
    propertyImage: 'https://images.unsplash.com/photo-1499793983690-e29da59ef1c2?w=80&h=80&fit=crop',
    checkIn: 'Sep 5, 2026',
    checkOut: 'Sep 8, 2026',
    lastMessage: 'Your booking is confirmed. See you soon!',
    lastTime: 'Yesterday',
    unread: 0,
    bookingRef: 'NDT-2026-0834',
    bookingStatus: 'confirmed',
    messages: [
      { id: 1, senderId: 'host', text: 'Hi there! Thanks for booking the Lakeside Retreat.', time: 'Yesterday 2:00 PM', read: true },
      { id: 2, senderId: 'me', text: 'We\'re looking forward to it! Do you allow pets?', time: 'Yesterday 2:30 PM', read: true },
      { id: 3, senderId: 'host', text: 'Yes, small pets are welcome with a small deposit. Your booking is confirmed. See you soon!', time: 'Yesterday 3:00 PM', read: true },
    ],
  },
  {
    id: 3,
    name: 'David Ochieng',
    avatar: 'D',
    role: 'host',
    property: 'Safari Lodge Murchison',
    propertyImage: 'https://images.unsplash.com/photo-1493246507139-91e8fad9978e?w=80&h=80&fit=crop',
    checkIn: 'Jul 10, 2026',
    checkOut: 'Jul 14, 2026',
    lastMessage: 'Hope you enjoyed your stay! Please leave a review.',
    lastTime: 'Aug 15',
    unread: 0,
    bookingRef: 'NDT-2026-0712',
    bookingStatus: 'completed',
    messages: [
      { id: 1, senderId: 'host', text: 'Welcome to Safari Lodge! The game drives start at 6 AM.', time: 'Jul 10 8:00 AM', read: true },
      { id: 2, senderId: 'me', text: 'Amazing! We saw lions on the first morning. Incredible experience.', time: 'Jul 11 7:30 AM', read: true },
      { id: 3, senderId: 'host', text: 'So glad you enjoyed it! Hope you enjoyed your stay! Please leave a review.', time: 'Aug 15 9:00 AM', read: true },
    ],
  },
  {
    id: 4,
    name: 'Amina Hassan',
    avatar: 'A',
    role: 'host',
    property: 'City Apartment Kampala',
    propertyImage: 'https://images.unsplash.com/photo-1522708323590-d24dbb6b0267?w=80&h=80&fit=crop',
    checkIn: 'Sep 20, 2026',
    checkOut: 'Sep 22, 2026',
    lastMessage: 'I\'ll send the check-in instructions closer to your arrival.',
    lastTime: 'Aug 18',
    unread: 1,
    bookingRef: 'NDT-2026-0901',
    bookingStatus: 'pending',
    messages: [
      { id: 1, senderId: 'me', text: 'Hi Amina! Just booked your apartment for September. Looking forward to it!', time: 'Aug 18 11:00 AM', read: true },
      { id: 2, senderId: 'host', text: 'Great to have you! I\'ll send the check-in instructions closer to your arrival.', time: 'Aug 18 12:00 PM', read: false },
    ],
  },
];

const statusColors: Record<string, string> = {
  confirmed: 'bg-green-100 text-green-700',
  pending: 'bg-amber-100 text-amber-700',
  completed: 'bg-gray-100 text-gray-600',
};

export default function MessagesPage() {
  const [selectedId, setSelectedId] = useState<number>(1);
  const [newMessage, setNewMessage] = useState('');
  const [convos, setConvos] = useState<Conversation[]>(conversations);
  const [mobileView, setMobileView] = useState<'list' | 'chat'>('list');
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const selected = convos.find((c) => c.id === selectedId)!;

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [selectedId, convos]);

  const handleSelect = (id: number) => {
    setSelectedId(id);
    setMobileView('chat');
    setConvos((prev) =>
      prev.map((c) => (c.id === id ? { ...c, unread: 0 } : c))
    );
  };

  const handleSend = () => {
    if (!newMessage.trim()) return;
    const msg: Message = {
      id: Date.now(),
      senderId: 'me',
      text: newMessage.trim(),
      time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      read: true,
    };
    setConvos((prev) =>
      prev.map((c) =>
        c.id === selectedId
          ? { ...c, messages: [...c.messages, msg], lastMessage: msg.text, lastTime: 'Just now' }
          : c
      )
    );
    setNewMessage('');
  };

  const totalUnread = convos.reduce((sum, c) => sum + c.unread, 0);

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <div className="pt-24 pb-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          {/* Page Header */}
          <div className="mb-6 flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900" style={{ fontFamily: '"Plus Jakarta Sans", sans-serif' }}>
                Messages
              </h1>
              <p className="text-gray-500 mt-1 text-sm">
                {totalUnread > 0 ? `${totalUnread} unread message${totalUnread > 1 ? 's' : ''}` : 'All caught up!'}
              </p>
            </div>
          </div>

          {/* Messaging Layout */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden" style={{ height: '70vh' }}>
            <div className="flex h-full">
              {/* Conversation List */}
              <div className={`w-full md:w-80 lg:w-96 border-r border-gray-100 flex flex-col flex-shrink-0 ${mobileView === 'chat' ? 'hidden md:flex' : 'flex'}`}>
                <div className="p-4 border-b border-gray-100">
                  <div className="relative">
                    <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                    <input
                      type="text"
                      placeholder="Search conversations..."
                      className="w-full pl-9 pr-4 py-2 bg-gray-50 rounded-xl text-sm border border-gray-200 focus:outline-none focus:ring-2 focus:ring-rose-300 focus:border-transparent"
                    />
                  </div>
                </div>
                <div className="flex-1 overflow-y-auto">
                  {convos.map((convo) => (
                    <button
                      key={convo.id}
                      onClick={() => handleSelect(convo.id)}
                      className={`w-full text-left px-4 py-4 border-b border-gray-50 hover:bg-gray-50 transition-colors ${selectedId === convo.id ? 'bg-[#4088C6]/10 border-l-2 border-l-[#4088C6]' : ''}`}
                    >
                      <div className="flex items-start gap-3">
                        <div className="relative flex-shrink-0">
                          <div className="w-11 h-11 rounded-full bg-gradient-to-br from-rose-400 to-rose-600 flex items-center justify-center text-white font-bold text-base">
                            {convo.avatar}
                          </div>
                          {convo.unread > 0 && (
                            <div className="absolute -top-1 -right-1 w-5 h-5 bg-[#4088C6] rounded-full flex items-center justify-center">
                              <span className="text-white text-xs font-bold">{convo.unread}</span>
                            </div>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between mb-0.5">
                            <span className={`text-sm font-semibold truncate ${convo.unread > 0 ? 'text-gray-900' : 'text-gray-700'}`}>
                              {convo.name}
                            </span>
                            <span className="text-xs text-gray-400 flex-shrink-0 ml-2">{convo.lastTime}</span>
                          </div>
                          <p className="text-xs text-gray-500 truncate mb-1">{convo.property}</p>
                          <p className={`text-xs truncate ${convo.unread > 0 ? 'text-gray-800 font-medium' : 'text-gray-400'}`}>
                            {convo.lastMessage}
                          </p>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Chat View */}
              <div className={`flex-1 flex flex-col ${mobileView === 'list' ? 'hidden md:flex' : 'flex'}`}>
                {/* Chat Header */}
                <div className="px-5 py-4 border-b border-gray-100 flex items-center gap-4">
                  <button
                    className="md:hidden p-1.5 rounded-lg hover:bg-gray-100 transition-colors"
                    onClick={() => setMobileView('list')}
                  >
                    <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                  </button>
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-rose-400 to-rose-600 flex items-center justify-center text-white font-bold">
                    {selected?.avatar}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-gray-900">{selected?.name}</p>
                    <p className="text-xs text-gray-500 truncate">{selected?.property}</p>
                  </div>
                  {/* Booking Context Badge */}
                  <div className="hidden sm:flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-xl px-3 py-2">
                    <div className="text-xs">
                      <p className="font-semibold text-gray-700">{selected?.bookingRef}</p>
                      <p className="text-gray-400">{selected?.checkIn} → {selected?.checkOut}</p>
                    </div>
                    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${statusColors[selected?.bookingStatus]}`}>
                      {selected?.bookingStatus}
                    </span>
                  </div>
                </div>

                {/* Booking Context Banner (mobile) */}
                <div className="sm:hidden mx-4 mt-3 bg-gray-50 border border-gray-200 rounded-xl px-3 py-2 flex items-center justify-between">
                  <div className="text-xs">
                    <span className="font-semibold text-gray-700">{selected?.bookingRef}</span>
                    <span className="text-gray-400 ml-2">{selected?.checkIn} → {selected?.checkOut}</span>
                  </div>
                  <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${statusColors[selected?.bookingStatus]}`}>
                    {selected?.bookingStatus}
                  </span>
                </div>

                {/* Messages */}
                <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
                  {selected?.messages.map((msg) => (
                    <div key={msg.id} className={`flex ${msg.senderId === 'me' ? 'justify-end' : 'justify-start'}`}>
                      {msg.senderId !== 'me' && (
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-rose-400 to-rose-600 flex items-center justify-center text-white text-xs font-bold mr-2 flex-shrink-0 mt-1">
                          {selected.avatar}
                        </div>
                      )}
                      <div className={`max-w-xs lg:max-w-md ${msg.senderId === 'me' ? 'items-end' : 'items-start'} flex flex-col`}>
                        <div
                          className={`px-4 py-2.5 rounded-2xl text-sm leading-relaxed ${
                            msg.senderId === 'me' ?'bg-[#4088C6] text-white rounded-br-sm' :'bg-gray-100 text-gray-800 rounded-bl-sm'
                          }`}
                        >
                          {msg.text}
                        </div>
                        <div className="flex items-center gap-1 mt-1">
                          <span className="text-xs text-gray-400">{msg.time}</span>
                          {msg.senderId === 'me' && (
                            <svg className={`w-3.5 h-3.5 ${msg.read ? 'text-rose-400' : 'text-gray-300'}`} fill="currentColor" viewBox="0 0 24 24">
                              <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" />
                            </svg>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                  <div ref={messagesEndRef} />
                </div>

                {/* Message Input */}
                <div className="px-5 py-4 border-t border-gray-100">
                  <div className="flex items-center gap-3">
                    <input
                      type="text"
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleSend()}
                      placeholder="Type a message..."
                      className="flex-1 px-4 py-3 bg-gray-50 rounded-xl text-sm border border-gray-200 focus:outline-none focus:ring-2 focus:ring-rose-300 focus:border-transparent"
                    />
                    <button
                      onClick={handleSend}
                      disabled={!newMessage.trim()}
                      className="bg-[#4088C6] hover:bg-[#2D6FA8] disabled:bg-gray-200 disabled:cursor-not-allowed text-white rounded-xl p-3 transition-colors flex-shrink-0"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
